import { createRouter, createWebHistory } from 'vue-router'
import Chat from '../components/Chat'
import NoGroups from '../components/NoGroups'
import LatestMessage from '../components/LatestMessage'
import LMSLaunch from '../components/LMSLaunch'
import { inviteRoute } from '@/components/Invite'
import { routes as orgRoutes } from '@/components/OrgManagement'
import store, { getChat, isSafari, getMyUser } from '@/store'
import { Search } from '@/components/Search'
import CreateGroup from '@/components/CreateGroup'
import GroupSettings from '@/components/GroupSettings/GroupSettings'
import GroupOptions from '@/components/GroupSettings/GroupOptions'
import GroupPeople from '@/components/GroupSettings/GroupPeople'
import GroupInvites from '@/components/GroupSettings/GroupInvites'
import GroupNotifications from '@/components/GroupSettings/GroupNotifications'
import MutualGroups from '@/components/MutualGroups'
import GroupMedia from '@/components/GroupSettings/GroupMedia'
import Announcements from '@/components/Announcements'
import Tasks from '@/components/Tasks'
import { routes as mainSettingsRoutes } from '@/components/MainSettings'
import { createLogger } from '@/helpers/log'
import { parseQueryParamArray } from '@/helpers/utils'
import i18n from '@/i18n'

const log = createLogger('ROUTER', true)

export const searchRoute = {
	name: 'search',
	path: '/search/:type?',
	// component: async () => {
	// 	const module = await import('@/components/Search')
	// 	return module.Search
	// },
	component: Search,
	meta: { titleKey: 'admin_people_search_action' },
	props: ({ params, query }) => ({
		searchType: params.type || 'messages',
		query: query.q,
		orderby: query.s,
		buster: query._,
		user_ids: parseQueryParamArray(query.users),
		bubble_ids: parseQueryParamArray(query.chats),
		start_date: query.sd,
		end_date: query.ed,
		relative_date_duration: query.duration,
	}),
}

export const groupSettings = {
	name: 'groupsettings',
	path: '/:chat/:id/settings',
	component: GroupSettings,
	meta: { titleKey: 'groupsettings_bubblesettings_title' },
	props: (route) => ({ bubbleId: route.params.id }),
	children: [
		{
			name: 'groupoptions',
			path: '',
			component: GroupOptions,
			props: (route) => ({ bubble_id: route.params.id }),
		},
		{
			name: 'grouppeople',
			path: 'users',
			component: GroupPeople,
			meta: { titleKey: 'admin_groups_members_label' },
			props: (route) => ({ bubbleId: route.params.id }),
		},
		{
			name: 'groupinvites',
			path: 'invites',
			component: GroupInvites,
			meta: { titleKey: 'groupinvites_title_invite' },
			props: (route) => ({
				bubbleId: route.params.id,
				showNew: !!route.query.new,
			}),
		},
		{
			name: 'groupnotifs',
			path: 'notifications',
			meta: { titleKey: 'groupsettings_notifications_title' },
			component: GroupNotifications,
		},
		{
			name: 'groupcommon',
			path: 'common',
			component: MutualGroups,
			meta: { titleKey: 'groupsettings_notifications_title' },
			props: (route) => {
				const chat = getChat(route.params.id)
				return { partner_id: chat?.dmpartner_id }
			},
		},
		{
			name: 'groupmedia',
			path: 'media/:type?',
			component: GroupMedia,
			meta: { titleKey: 'groupsettings_media_title' },
			props: (route) => {
				const { id, type } = route.params
				return { chatId: id, type }
			},
		},
	],
}

export const chatRoute = {
	name: 'chat',
	path: '/:chat/:id',
	component: Chat,
	meta: {
		getTitle: (route) => {
			return getChat(route.params.id)?.displayname || null
		},
	},
}

export const threadRoute = {
	name: 'chatthread',
	path: '/:chat/:id/thread/:parentMsgId',
	component: Chat,
	meta: {
		getTitle: (route) => {
			return getChat(route.params.id)?.displayname || null
		},
	},
	props: (route) => ({
		chatId: route.params.id,
		parentMsgId: route.params.parentMsgId,
	}),
}

const routes = [
	...orgRoutes,
	{ name: 'nogroups', path: '/group/none', component: NoGroups },
	{ name: 'latest', path: '/latest/:id', component: LatestMessage },
	searchRoute,
	inviteRoute,
	{
		name: 'announcements',
		path: '/announcements/:id?',
		meta: { titleKey: 'announcements_name_multiple' },
		// component: () => import('@/components/Announcements'),
		component: Announcements,
	},
	{
		name: 'tasks',
		path: '/tasks',
		meta: { titleKey: 'tasks_drawer_title' },
		// component: () => import('@/components/Tasks'),
		component: Tasks,
	},
	...mainSettingsRoutes,
	{ name: 'external_launch', path: '/external_launch', component: LMSLaunch },
	{
		name: 'creategroup',
		path: '/chat/create',
		meta: { titleKey: 'chat_drawer_addbubble_create' },
		component: CreateGroup,
	},
	chatRoute,
	groupSettings,
	threadRoute,
]

const router = createRouter({
	history: createWebHistory(),
	routes,
})

router.beforeEach((to, from, next) => {
	// Check if the route requires a specific user permission
	if (to.meta.requiresPermission) {
		const user = getMyUser()
		// Check if user has the required permission
		if (!user || !user[to.meta.requiresPermission]) {
			next({ name: 'overview' })
			return
		}
	}

	// Continue with the navigation
	next()
})

router.afterEach((to) => {
	setDocTitleFromRoute(to)
})

const setDocTitleFromRoute = (toRoute) => {
	// Stolen from: https://stackoverflow.com/a/73244339
	let docTitle = null
	const route = getRouteWithMeta(toRoute)
	if (!route) return
	const { title, titleKey, getTitle } = route.meta
	if (title) {
		docTitle = title
	} else if (titleKey) {
		const title = i18n.global.t(titleKey)
		docTitle = title === titleKey ? null : title
	} else if (getTitle) {
		docTitle = getTitle(route)
	}
	const parts = docTitle ? [docTitle, '|', 'Pronto'] : ['Pronto']
	if (isSafari) {
		const badgeCount = store.calcAllExternalUnreads()
		if (badgeCount > 0) {
			parts.unshift(`(${badgeCount})`)
		}
	}
	// document titles require funky spacing https://stackoverflow.com/q/30833402
	docTitle = parts.join('\u205f\u205f\u205f')
	log('setting document title', docTitle)
	document.title = docTitle
}

export const setDocTitleFromCurrentRoute = () => {
	return setDocTitleFromRoute(router.currentRoute?._value)
}

const getRouteWithMeta = (route) => {
	if (!route) return null
	if (route.meta) return route
	if (route.matched?.length)
		return to.matched
			.slice()
			.reverse()
			.find((r) => r.meta && Object.keys(r.meta).length)
	return null
}

export default router
